import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate, useParams } from 'react-router-dom';
import FullscreenError from '@/components/utils/FullscreenError';
import CreateVideoModal from '@/components/admin/academy/CreateVideoModal.tsx';
import EditVideoModal from '@/components/admin/academy/EditVideoModal.tsx';
import MoveVideoModal from '@/components/admin/academy/MoveVideoModal.tsx';
import EditModuleModal from '@/components/admin/academy/EditModuleModal.tsx';
import VideoKnowledgeModal from '@/components/modals/VideoKnowledgeModal';
import PublishModal from '@/components/PublishModal';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import { faSpinner, faArrowLeft, faPlus, faVideo, faEdit, faTrash, faRocket, faEyeSlash } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import AdminVideoCard from '@/components/admin/academy/AdminVideoCard';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import type { Course, Module, Video, CourseID, ModuleID, VideoID } from '@/models';

const PAGE_SIZE = 50;

export default function AdminModuleDetail() {
  const { isStaff } = useUser();
  const navigate = useNavigate();
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const [course, setCourse] = useState<Course | null>(null);
  const [module, setModule] = useState<Module | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [isCreateVideoModalOpen, setIsCreateVideoModalOpen] = useState(false);
  const [isEditVideoModalOpen, setIsEditVideoModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<Video | null>(null);
  const [isMoveVideoModalOpen, setIsMoveVideoModalOpen] = useState(false);
  const [movingVideo, setMovingVideo] = useState<Video | null>(null);
  const [deletingVideo, setDeletingVideo] = useState<VideoID | null>(null);
  const [isKnowledgeModalOpen, setIsKnowledgeModalOpen] = useState(false);
  const [knowledgeVideo, setKnowledgeVideo] = useState<Video | null>(null);
  const [isEditModuleModalOpen, setIsEditModuleModalOpen] = useState(false);
  const [isPublishModuleModalOpen, setIsPublishModuleModalOpen] = useState(false);
  const [publishingModule, setPublishingModule] = useState(false);
  const [deletingModule, setDeletingModule] = useState(false);

  useEffect(() => {
    if (courseId && moduleId) {
      fetchData();
    }
  }, [courseId, moduleId, currentPage]);

  const fetchData = async () => {
    if (!courseId || !moduleId) {
      console.error('Missing courseId or moduleId:', { courseId, moduleId });
      return;
    }

    console.log('Fetching module and videos for:', { courseId, moduleId });

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      // Fetch course, module, and videos in parallel
      const [courseResponse, moduleResponse, videosResponse] = await Promise.all([
        APIRoutes.getCourse(courseId as CourseID),
        APIRoutes.getModule(moduleId as ModuleID),
        APIRoutes.listVideos(moduleId as ModuleID, {
          limit: PAGE_SIZE,
          next: currentPage > 1 ? `$${currentPage}` : undefined,
          includeHiddenByModuleLimit: true
        })
      ]);

      console.log('API responses:', { courseResponse, moduleResponse, videosResponse });

      setCourse(courseResponse);
      setModule(moduleResponse);
      setVideos(videosResponse.videos);
      setTotalVideos(videosResponse.total);
    } catch (err) {
      console.error('Critical error loading module details:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Moduldetails');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoCreated = (newVideo: Video) => {
    setVideos(prev => [...prev, newVideo]);
    setTotalVideos(prev => prev + 1);
    if (module) {
      setModule(prev => prev ? { ...prev, videoCount: (prev.videoCount || 0) + 1 } : prev);
    }
    showSuccessToast('Video erfolgreich erstellt');
    //fetchData(); // Reload data to get correct order
  };

  const handleEditVideo = (video: Video) => {
    setEditingVideo(video);
    setIsEditVideoModalOpen(true);
  };

  const handleMoveVideo = (video: Video) => {
    setMovingVideo(video);
    setIsMoveVideoModalOpen(true);
  };

  const handleVideoMoved = (movedVideo: Video) => {
    // The video moved to a different module - remove it from the current list
    if (module !== null && movedVideo.moduleID !== module.id) {
      setVideos(prev => prev.filter(video => video.id !== movedVideo.id));
      setTotalVideos(prev => prev - 1);
      setModule(prev => prev ? { ...prev, videoCount: Math.max((prev.videoCount || 0) - 1, 0) } : prev);
    }
  };

  const handleVideoUploaded = (uploadedVideo: Video) => {
    if (!uploadedVideo) {
      console.error('handleVideoUploaded called with undefined video');
      return;
    }

    if (!uploadedVideo.id) {
      console.error('handleVideoUploaded called with video missing id:', uploadedVideo);
      return;
    }

    setVideos(prevVideos =>
      prevVideos.map(video =>
        video.id === uploadedVideo.id ? uploadedVideo : video
      )
    );
    showSuccessToast('Video erfolgreich hochgeladen - Verarbeitung gestartet');
  };

  const handleVideoUpdated = (uploadedVideo: Video) => {
      if (!uploadedVideo) {
          console.error('handleVideoUploaded called with undefined video');
          return;
      }

      if (!uploadedVideo.id) {
          console.error('handleVideoUploaded called with video missing id:', uploadedVideo);
          return;
      }

      //fetchData(); // Reload data to get correct order
  };

  const handleDeleteVideo = async (videoId: VideoID) => {
    if (!courseId || !moduleId) return;

    if (!confirm('Sind Sie sicher, dass Sie dieses Video löschen möchten?')) {
      return;
    }

    try {
      setDeletingVideo(videoId);
      await APIRoutes.deleteVideo(videoId);
      setVideos(prev => prev.filter(video => video.id !== videoId));
      setTotalVideos(prev => prev - 1);
      if (module) {
        setModule(prev => prev ? { ...prev, videoCount: (prev.videoCount || 0) - 1 } : prev);
      }
      showSuccessToast('Video erfolgreich gelöscht');
    } catch (err) {
      console.error('Error deleting video:', err);
      showErrorToast('Fehler beim Löschen des Videos');
      handleAPIError(err);
    } finally {
      setDeletingVideo(null);
    }
  };

  const handleShowKnowledge = (video: Video) => {
    setKnowledgeVideo(video);
    setIsKnowledgeModalOpen(true);
  };

  const handleModuleHeaderUpdated = (updatedModule: Module) => {
    if (updatedModule !== null && updatedModule.id) {
      setModule(updatedModule);
      showSuccessToast('Modul erfolgreich aktualisiert');
    }
  };

  const handlePublishModule = async () => {
    if (module === null) return;

    try {
      setPublishingModule(true);
      const updatedModule = await APIRoutes.publishModule(module.id);
      setModule(updatedModule);
      showSuccessToast('Modul erfolgreich veröffentlicht');
    } catch (err) {
      console.error('Error publishing module:', err);
      showErrorToast('Fehler beim Veröffentlichen des Moduls');
      handleAPIError(err);
      throw err;
    } finally {
      setPublishingModule(false);
    }
  };

  const handleUnpublishModule = async () => {
    if (module === null) return;
    if (!confirm('Möchten Sie dieses Modul wirklich zurückziehen?')) {
      return;
    }

    try {
      setPublishingModule(true);
      const updatedModule = await APIRoutes.unpublishModule(module.id);
      setModule(updatedModule);
      showSuccessToast('Modul erfolgreich zurückgezogen');
    } catch (err) {
      console.error('Error unpublishing module:', err);
      showErrorToast('Fehler beim Zurückziehen des Moduls');
      handleAPIError(err);
    } finally {
      setPublishingModule(false);
    }
  };

  const handleDeleteModule = async () => {
    if (module === null || courseId === undefined) return;
    if (!confirm('Sind Sie sicher, dass Sie dieses Modul löschen möchten? Alle Videos werden ebenfalls gelöscht.')) {
      return;
    }

    try {
      setDeletingModule(true);
      await APIRoutes.deleteModule(module.id);
      showSuccessToast('Modul erfolgreich gelöscht');
      navigate(`/admin/courses/${courseId}`);
    } catch (err) {
      console.error('Error deleting module:', err);
      showErrorToast('Fehler beim Löschen des Moduls');
      handleAPIError(err);
      setDeletingModule(false);
    }
  };

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Modul nicht verfügbar"
        message={error || 'Die Moduldetails konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchData}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <Link
              to={`/admin/courses/${courseId}`}
              className="mr-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
            </Link>
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                {course?.title}
              </div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                {loading ? 'Lade Modul...' : module?.title || 'Modul nicht gefunden'}
              </h1>
            </div>
          </div>
          {module !== null && (
            <div className="flex items-center space-x-2">
              {module.published ? (
                <button
                  onClick={handleUnpublishModule}
                  disabled={!isStaff || publishingModule}
                  title={isStaff ? 'Modul zurückziehen' : 'Nur für Mitarbeiter'}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg border border-yellow-300 dark:border-yellow-700 text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 transition-colors ${!isStaff || publishingModule ? 'opacity-50 cursor-not-allowed' : 'hover:bg-yellow-100 dark:hover:bg-yellow-900/40 cursor-pointer'}`}
                >
                  <FontAwesomeIcon icon={publishingModule ? faSpinner : faEyeSlash} className={`w-4 h-4 ${publishingModule ? 'animate-spin' : ''}`} />
                  <span>Zurückziehen</span>
                </button>
              ) : (
                <button
                  onClick={() => setIsPublishModuleModalOpen(true)}
                  disabled={!isStaff || publishingModule}
                  title={isStaff ? 'Modul veröffentlichen' : 'Nur für Mitarbeiter'}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 transition-colors ${!isStaff || publishingModule ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-100 dark:hover:bg-green-900/40 cursor-pointer'}`}
                >
                  <FontAwesomeIcon icon={publishingModule ? faSpinner : faRocket} className={`w-4 h-4 ${publishingModule ? 'animate-spin' : ''}`} />
                  <span>Veröffentlichen</span>
                </button>
              )}
              <button
                onClick={() => setIsEditModuleModalOpen(true)}
                disabled={!isStaff}
                title={isStaff ? 'Modul bearbeiten' : 'Nur für Mitarbeiter'}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 transition-colors ${!isStaff ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                <span>Bearbeiten</span>
              </button>
              <button
                onClick={handleDeleteModule}
                disabled={!isStaff || deletingModule}
                title={isStaff ? 'Modul löschen' : 'Nur für Mitarbeiter'}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 transition-colors ${!isStaff || deletingModule ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-100 dark:hover:bg-red-900/40 cursor-pointer'}`}
              >
                <FontAwesomeIcon icon={deletingModule ? faSpinner : faTrash} className={`w-4 h-4 ${deletingModule ? 'animate-spin' : ''}`} />
                <span>Löschen</span>
              </button>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && !criticalError && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!loading && module && (
          <>
            {/* Module Overview */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {module.thumbnail && (
                  <div className="lg:col-span-1">
                    <div className="aspect-video relative overflow-hidden rounded-lg">
                      <img
                        src={APIRoutes.getBlobContentUrl(module.thumbnail)}
                        alt={module.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
                <div className={module.thumbnail ? "lg:col-span-2" : "lg:col-span-3"}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {module.title}
                      </h2>
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        module.published 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {module.published ? 'Veröffentlicht' : 'Entwurf'}
                      </span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <MarkdownRenderer>
                      {module.description}
                    </MarkdownRenderer>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="block font-medium text-gray-500 dark:text-gray-400">Videos</span>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{module.videoCount}</span>
                    </div>
                    <div>
                      <span className="block font-medium text-gray-500 dark:text-gray-400">Reihenfolge</span>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{module.order}</span>
                    </div>
                    <div>
                      <span className="block font-medium text-gray-500 dark:text-gray-400">Erstellt</span>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">{formatDate(module.created)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Videos Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Videos ({totalVideos})
                  </h3>
                  <button
                    onClick={() => setIsCreateVideoModalOpen(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors cursor-pointer"
                  >
                    <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                    <span>Neues Video</span>
                  </button>
                </div>
              </div>

              <div className="p-6">
                {videos.length === 0 ? (
                  <div className="text-center py-12">
                    <FontAwesomeIcon icon={faVideo} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
                    <h4 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
                      Keine Videos gefunden
                    </h4>
                    <p className="text-gray-500 dark:text-gray-500 mb-6">
                      Erstellen Sie das erste Video für dieses Modul.
                    </p>
                    <button
                      onClick={() => setIsCreateVideoModalOpen(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors cursor-pointer"
                    >
                      Video erstellen
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      {videos.filter(video => video !== null && video !== undefined).map((video) => (
                        <AdminVideoCard
                          key={video.id}
                          video={video}
                          onVideoUploaded={handleVideoUploaded}
                          onVideoUpdated={handleVideoUpdated}
                          onEditVideo={handleEditVideo}
                          onMoveVideo={handleMoveVideo}
                          onDeleteVideo={handleDeleteVideo}
                          deletingVideo={deletingVideo}
                          onShowKnowledge={handleShowKnowledge}
                          moduleNoKnowledge={module.noKnowledge}
                        />
                      ))}
                    </div>
                    <PageBasedPagination
                      currentPage={currentPage}
                      totalCount={totalVideos}
                      pageSize={PAGE_SIZE}
                      onPageChange={setCurrentPage}
                      loading={loading}
                    />
                  </>
                )}
              </div>
            </div>
          </>
        )}

      {/* Modals */}
      {module && (
        <>
          <CreateVideoModal
            isOpen={isCreateVideoModalOpen}
            onClose={() => setIsCreateVideoModalOpen(false)}
            moduleId={module.id}
            onVideoCreated={handleVideoCreated}
            moduleThumbnail={module.thumbnail ?? undefined}
          />

          <EditVideoModal
            isOpen={isEditVideoModalOpen}
            onClose={() => setIsEditVideoModalOpen(false)}
            courseId={module.courseID}
            moduleId={module.id}
            video={editingVideo}
            onVideoUpdated={handleVideoUpdated}
            moduleNoKnowledge={module.noKnowledge}
          />

          <MoveVideoModal
            isOpen={isMoveVideoModalOpen}
            onClose={() => setIsMoveVideoModalOpen(false)}
            video={movingVideo}
            onVideoMoved={handleVideoMoved}
          />

          {knowledgeVideo && (
            <VideoKnowledgeModal
              isOpen={isKnowledgeModalOpen}
              onClose={() => {
                setIsKnowledgeModalOpen(false);
                setKnowledgeVideo(null);
              }}
              video={knowledgeVideo}
              onVideoUpdated={(updatedVideo) => {
                handleVideoUpdated(updatedVideo);
                setKnowledgeVideo(updatedVideo);
              }}
            />
          )}

          <EditModuleModal
            isOpen={isEditModuleModalOpen}
            onClose={() => setIsEditModuleModalOpen(false)}
            courseId={module.courseID}
            module={module}
            onModuleUpdated={handleModuleHeaderUpdated}
          />

          <PublishModal
            isOpen={isPublishModuleModalOpen}
            onClose={() => setIsPublishModuleModalOpen(false)}
            onPublish={handlePublishModule}
            title="Modul veröffentlichen"
            itemName={module.title}
            type="module"
          />
        </>
      )}
    </>
  );
}
